/* global gapi google */
import { useState, useEffect } from 'react';
import { toast } from "../../utils/appToast";

const SCOPES = 'https://www.googleapis.com/auth/drive.file';
const CLIENT_ID = "874960627809-ganpcetmlj68obe4b15qmg5fu9beoqkb.apps.googleusercontent.com";
const API_KEY = "AIzaSyARXJnnlScL7GoRdoYkpQUKQ2sqcNTMnFc";
const APP_ID = "874960627809";

export default function useGooglePicker() {
  const [accessToken, setAccessToken] = useState(null);
  const [pickerLoaded, setPickerLoaded] = useState(false);
  const [gisLoaded, setGisLoaded] = useState(false);
  const [tokenClient, setTokenClient] = useState(null);

  // ✅ 1. Load token from localStorage on init and validate it
  useEffect(() => {
    const storedToken = localStorage.getItem("gdrive_token");
    if (storedToken) {
      // Check if token is valid by making a simple API call
      if (window.gapi) {
        gapi.client.init({
          apiKey: API_KEY,
          discoveryDocs: ["https://www.googleapis.com/discovery/v1/apis/drive/v3/rest"],
        }).then(() => {
          gapi.client.setToken({ access_token: storedToken });
          return gapi.client.drive.files.list({ pageSize: 1 }); // lightweight request to validate token
        })
        .then(() => {
          setAccessToken(storedToken);
        })
        .catch((err) => {
          // Token is invalid or expired
          localStorage.removeItem("gdrive_token");
          setAccessToken(null);
          toast.info("Google Drive token expired. Please connect again.");
        });
      }
    }
  }, []);

  useEffect(() => {
    const loadApis = () => {
      const script1 = document.createElement('script');
      script1.src = 'https://apis.google.com/js/api.js';
      script1.onload = () => {
        if (window.gapi) {
          gapi.load('client:picker', initPicker);
        } else {
          console.error("gapi not available after script load");
        }
      };

      const script2 = document.createElement('script');
      script2.src = 'https://accounts.google.com/gsi/client';
      script2.onload = () => initGis();

      document.body.appendChild(script1);
      document.body.appendChild(script2);
    };

    const initPicker = async () => {
      try {
        if (window.gapi) {
          await gapi.client.load('https://www.googleapis.com/discovery/v1/apis/drive/v3/rest');
          setPickerLoaded(true);
        } else {
          console.error("gapi client not available for picker initialization");
        }
      } catch (error) {
        console.error("Picker init error:", error);
      }
    };

    const initGis = () => {
      try {
        const client = google.accounts.oauth2.initTokenClient({
          client_id: CLIENT_ID,
          scope: SCOPES,
          callback: (tokenResponse) => {
            if (tokenResponse?.access_token) {
              setAccessToken(tokenResponse.access_token);
              localStorage.setItem("gdrive_token", tokenResponse.access_token);
              toast.success("Connected to Google Drive!");
            } else {
              toast.error("Google authentication failed.");
            }
          },
        });
        setTokenClient(client);
        setGisLoaded(true);
      } catch (error) {
        toast.error("Failed to initialize Google OAuth client.");
        console.error("GIS init error:", error);
      }
    };

    loadApis();
  }, []);

  const connect = () => {
    if (tokenClient) {
      tokenClient.requestAccessToken({ prompt: 'consent' });
    } else {
      toast.error("Token client not ready yet.");
    }
  };

  const disconnect = () => {
    if (accessToken) {
      google.accounts.oauth2.revoke(accessToken, () => {
        setAccessToken(null);
        localStorage.removeItem("gdrive_token"); // ✅ 3. Remove token
        toast.success("Disconnected from Google Drive.");
      });
    }
  };

  const openPicker = (onFilesPicked) => {
    if (!accessToken) {
      toast.error("Access token missing. Connect to Google Drive first.");
      return;
    }

    if (!pickerLoaded) {
      return;
    }

    try {
      const view = new google.picker.View(google.picker.ViewId.DOCS);
      view.setMimeTypes('application/pdf,image/png,image/jpeg,image/jpg');

      const picker = new google.picker.PickerBuilder()
        .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
        .setOAuthToken(accessToken)
        .setDeveloperKey(API_KEY)
        .setAppId(APP_ID)
        .addView(view)
        .addView(new google.picker.DocsUploadView())
        .setCallback((data) => {
          if (data.action === google.picker.Action.PICKED) {
            console.log("📄 Files selected:", data.docs);

            if (typeof onFilesPicked === 'function') {
              onFilesPicked(data.docs);
            } else {
              console.warn("⚠️ No onFilesPicked callback provided. Ignoring result.");
            }
          } else if (data.action === google.picker.Action.CANCEL) {
            // toast.info("File selection cancelled.");
          }
        })
        .build();

      picker.setVisible(true);
    } catch (error) {
      console.error("Picker error:", error);
    }
  };

  return {
    isConnected: !!accessToken,
    connect,
    disconnect,
    openPicker,
    accessToken,
  };
}
